import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Waves } from '@phosphor-icons/react';

export function Register() {
  const [formData, setFormData] = useState({
    email: '', password: '', name: '', company_name: '',
    registration_number: '', vat_number: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(formData);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #0a1f3d 0%, #0d3b66 50%, #1976d2 100%)'
    }}>
      <div className="hidden lg:flex relative items-end p-12">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full opacity-40 wave-float" style={{background: 'radial-gradient(circle, #06aed5 0%, transparent 70%)'}}></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full opacity-30 wave-float" style={{background: 'radial-gradient(circle, #4ea8de 0%, transparent 70%)', animationDelay: '2s'}}></div>
        </div>
        
        <div className="relative z-10 text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-xl border border-white/30 flex items-center justify-center text-3xl font-bold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>S</div>
            <div>
              <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>SAIL Books</h1>
              <p className="text-xs uppercase tracking-widest text-cyan-200/80 font-semibold">South African Information Logistics</p>
            </div>
          </div>
          
          <h2 className="text-5xl font-bold leading-tight mb-4 text-white" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            Start your<br/>journey<br/>today
          </h2>
          <p className="text-lg text-cyan-100/90 leading-relaxed">
            Join hundreds of South African businesses sailing smoothly through SARS, BEE, CIPC and daily bookkeeping.
          </p>
          
          <div className="mt-12 flex items-center gap-2 text-cyan-200/70 text-sm">
            <Waves size={20} weight="fill" />
            <span>14-day free trial. No credit card required.</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-md glass-card p-10">
          <div className="mb-6">
            <div className="lg:hidden flex items-center gap-2.5 mb-6">
              <div className="w-10 h-10 rounded-lg ocean-gradient flex items-center justify-center text-white font-bold text-xl" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>S</div>
              <h1 className="text-2xl font-bold ocean-text" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>SAIL Books</h1>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Create account</h2>
            <p className="mt-2 text-slate-600">Hoist the sails and let's begin</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div data-testid="register-error" className="p-3 rounded-lg border border-red-200 bg-red-50/80 backdrop-blur-sm text-red-800 text-sm">
                {error}
              </div>
            )}

            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Full Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="register-name-input" className="mt-2 bg-white/60 border-slate-300 focus:border-blue-700" />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="register-email-input" className="mt-2 bg-white/60 border-slate-300 focus:border-blue-700" />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Password</Label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="register-password-input" className="mt-2 bg-white/60 border-slate-300 focus:border-blue-700" />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Company Name</Label>
              <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} required data-testid="register-company-input" className="mt-2 bg-white/60 border-slate-300 focus:border-blue-700" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Reg #</Label>
                <Input value={formData.registration_number} onChange={(e) => setFormData({...formData, registration_number: e.target.value})} data-testid="register-reg-number-input" className="mt-2 bg-white/60 border-slate-300 focus:border-blue-700" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">VAT #</Label>
                <Input value={formData.vat_number} onChange={(e) => setFormData({...formData, vat_number: e.target.value})} data-testid="register-vat-number-input" className="mt-2 bg-white/60 border-slate-300 focus:border-blue-700" />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full ocean-gradient text-white hover:opacity-90 h-12 font-semibold rounded-lg shadow-lg transition-all"
            >
              {loading ? 'Setting sail...' : 'Set sail'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Already on board?{' '}
            <Link to="/login" className="font-semibold text-blue-700 hover:underline" data-testid="go-to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
